import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from './config/api';
import Spinner from 'react-bootstrap/Spinner';
import { FaArrowLeft, FaWrench, FaExclamationTriangle, FaChevronDown, FaPlus, FaTimes, FaCamera, FaPaperclip, FaEllipsisV, FaTrash, FaEdit, FaCog, FaFileAlt, FaExternalLinkAlt, FaCalendarAlt, FaUser, FaStickyNote, FaUserTie, FaLink } from 'react-icons/fa';
import { managerPreviewUrl, managerEquipmentPreviewUrl, managerIssueLink, managerVisitLink, copyToClipboard } from './managerPreviewLinks';
import MechInternalFilesField from './MechInternalFilesField';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const PRIORITY_STYLES = {
  high: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca', label: 'High' },
  medium: { bg: '#fffbeb', color: '#d97706', border: '#fde68a', label: 'Medium' },
  low: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', label: 'Low' },
};
const PRIORITIES = [
  { key: 'high', label: 'High', emoji: '🔴', value: 'high 🔴' },
  { key: 'medium', label: 'Medium', emoji: '🟡', value: 'medium 🟡' },
  { key: 'low', label: 'Low', emoji: '🟢', value: 'low 🟢' },
];
const TECHNICIANS = ['Ehsan', 'Gregor', 'Rowan', 'Ergi', 'Hooman', 'Kennedy', 'Mateo', 'Donny'];
// Keep in step with KNOWN_EQUIPMENT_CATEGORIES in the server's
// mechanical_room_routes, which is what the Equipment Scopes editor lists.
// MAU was in the field on four units but missing here, so whoever added them
// typed the category by hand and one came out "MUA" — same equipment, second
// spelling, its own scope.
const EQUIPMENT_CATEGORIES = ['HVAC', 'Boiler', 'Chiller', 'Pump', 'Fan', 'Exhaust Fan', 'AHU', 'RTU', 'MAU', 'VFD', 'Cooling Tower', 'DHW', 'Electrical', 'Plumbing', 'Water Treatment', 'Other'];

// ── PDF Viewer Modal ─────────────────────────────────────────────
function PdfViewerModal({ open, url, title, onClose }) {
  if (!open || !url) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', padding: '1rem' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: 900, height: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#111827' }}>{title}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <a href={url} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}>
              <FaExternalLinkAlt style={{ fontSize: '0.75rem' }} /> Open in New Tab
            </a>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem', color: '#6b7280', padding: '0.25rem' }}><FaTimes /></button>
          </div>
        </div>
        <iframe src={url} title={title} style={{ flex: 1, border: 'none', width: '100%' }} />
      </div>
    </div>
  );
}

// ── Image Lightbox (carousel) ────────────────────────────────────
function ImageLightbox({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex || 0);
  useEffect(() => { setIdx(startIndex || 0); }, [startIndex]);

  if (startIndex === null || startIndex === undefined || !images || images.length === 0) return null;

  const go = (delta, e) => {
    e.stopPropagation();
    setIdx((prev) => (prev + delta + images.length) % images.length);
  };

  const arrowStyle = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    width: 48, height: 48, borderRadius: '50%', border: 'none',
    background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 700,
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: '1rem',
    }}>
      <img src={images[idx]} alt="" style={{ maxWidth: '92%', maxHeight: '88vh', borderRadius: 8, objectFit: 'contain' }} />
      {images.length > 1 && (
        <>
          <button onClick={(e) => go(-1, e)} style={{ ...arrowStyle, left: '1rem' }}>‹</button>
          <button onClick={(e) => go(1, e)} style={{ ...arrowStyle, right: '1rem' }}>›</button>
          <div style={{ position: 'absolute', bottom: '1.5rem', left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: '0.95rem', fontWeight: 600, background: 'rgba(0,0,0,0.5)', padding: '0.35rem 0.85rem', borderRadius: '999px' }}>
            {idx + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
}

function CollapsibleQuotation({ scopeOfWork, setScopeOfWork, materialsNeeded, setMaterialsNeeded, laborNeeded, setLaborNeeded, quotingTechnician, setQuotingTechnician, quotationEmail, setQuotationEmail, internalFiles, setInternalFiles, onAddInternalFiles }) {
  // Opens on its own when there is already something in here, so an edit can't
  // hide an attachment behind a collapsed row.
  const [expanded, setExpanded] = useState(() => internalFiles.length > 0);
  return (
    <div style={{ border: '2px solid #e5e7eb', borderRadius: '12px', margin: '0 0 1.15rem', background: '#fafbfc', overflow: 'hidden' }}>
      <button type="button" onClick={() => setExpanded((e) => !e)}
        style={{ width: '100%', padding: '1rem', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span style={{ fontSize: '1rem' }}>📝</span>
          <span style={{ fontSize: '1rem', fontWeight: 600, color: '#333' }}>Make Quotation (For Hudson Office)</span>
          <span style={{ fontSize: '0.85rem', color: '#9ca3af', marginLeft: '0.25rem' }}>Optional</span>
        </div>
        <FaChevronDown style={{ color: '#6b7280', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </button>
      {expanded && (
        <div style={{ padding: '0 1rem 1rem' }}>
          <div style={{ marginBottom: '0.85rem' }}><label style={LABEL_STYLE}>Scope of Work</label><textarea value={scopeOfWork} onChange={(e) => setScopeOfWork(e.target.value)} placeholder="Scope of work for repair..." rows={2} style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 60 }} /></div>
          <div style={{ marginBottom: '0.85rem' }}><label style={LABEL_STYLE}>Materials Needed</label><textarea value={materialsNeeded} onChange={(e) => setMaterialsNeeded(e.target.value)} placeholder="Materials, lead time, price..." rows={2} style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 50 }} /></div>
          <div style={{ marginBottom: '0.85rem' }}><label style={LABEL_STYLE}>Labor Needed</label><textarea value={laborNeeded} onChange={(e) => setLaborNeeded(e.target.value)} placeholder="Labor hours, # of techs..." rows={2} style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 50 }} /></div>
          <div style={{ marginBottom: '0.85rem' }}><label style={LABEL_STYLE}>Technician Quoting</label><select value={quotingTechnician} onChange={(e) => setQuotingTechnician(e.target.value)} style={{ ...INPUT_STYLE, cursor: 'pointer' }}><option value="">Select technician...</option>{TECHNICIANS.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
          <div style={{ marginBottom: '0.85rem' }}><label style={LABEL_STYLE}>Email copy (optional)</label><input type="email" value={quotationEmail} onChange={(e) => setQuotationEmail(e.target.value)} placeholder="Technician email" style={INPUT_STYLE} /></div>
          <MechInternalFilesField files={internalFiles} setFiles={setInternalFiles} onAdd={onAddInternalFiles} />
        </div>
      )}
    </div>
  );
}

async function compressImage(file, maxWidth = 1200, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = (h * maxWidth) / w; w = maxWidth; }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        const size = Math.round((dataUrl.length - 'data:image/jpeg;base64,'.length) * 0.75);
        resolve({ name: file.name, dataUrl, size });
      };
      img.onerror = reject; img.src = e.target.result;
    };
    reader.onerror = reject; reader.readAsDataURL(file);
  });
}

// ── Modal Shell ──────────────────────────────────────────────────
function Modal({ open, onClose, title, children, warnOnClose }) {
  if (!open) return null;
  const safeClose = () => {
    if (warnOnClose && !window.confirm('You have unsaved changes. Are you sure you want to close?')) return;
    onClose();
  };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', overflowY: 'auto', padding: '2rem 1rem' }}
      onClick={(e) => { if (e.target === e.currentTarget) safeClose(); }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: 600, padding: '1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>{title}</h2>
          <button onClick={safeClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem', color: '#6b7280', padding: '0.25rem' }}><FaTimes /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── 3-dots menu item ─────────────────────────────────────────────
function MenuItem({ icon, label, color = '#374151', hoverBg = '#f0f7ff', bordered = true, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{ width: '100%', padding: '0.7rem 1rem', background: 'none', border: 'none', borderBottom: bordered ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color, fontWeight: 600, fontSize: '0.95rem', textAlign: 'left', whiteSpace: 'nowrap' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
    >
      {icon} {label}
    </button>
  );
}

// ── Visits Table ─────────────────────────────────────────────────
const VISIT_GRID = '2fr 1fr 1fr 36px';
function VisitsTable({ visits, onClickVisit, onDeleteVisit, onEditVisit, buildingIdStr, onCopied }) {
  const [menuOpen, setMenuOpen] = useState(null);

  if (visits.length === 0) return <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', fontSize: '1rem' }}>No visits yet.</div>;
  return (
    <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'visible', position: 'relative' }}>
      <div style={{ display: 'grid', gridTemplateColumns: VISIT_GRID, padding: '0.75rem 1.15rem', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Visit</div>
        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Technician</div>
        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Date</div>
        <div />
      </div>
      {visits.map((v, idx) => (
        <div key={v.id} style={{
          display: 'grid', gridTemplateColumns: VISIT_GRID, padding: '0.9rem 1.15rem', cursor: 'pointer', alignItems: 'center',
          borderBottom: idx < visits.length - 1 ? '1px solid #f3f4f6' : 'none', transition: 'background 0.1s', position: 'relative',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f7ff'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
          <div onClick={() => onClickVisit(v)} style={{ fontWeight: 600, color: '#111827', fontSize: '1.05rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '0.5rem' }}>{v.name}</div>
          <div onClick={() => onClickVisit(v)} style={{ color: '#374151', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.technician || '—'}</div>
          <div onClick={() => onClickVisit(v)} style={{ color: '#6b7280', fontSize: '1rem' }}>{formatDate(v.dateVisited)}</div>
          <div style={{ position: 'relative' }}>
            <button onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === v.id ? null : v.id); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '0.25rem', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaEllipsisV />
            </button>
            {menuOpen === v.id && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 50 }} onClick={() => setMenuOpen(null)} />
                <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 51, background: '#fff', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', minWidth: 180, overflow: 'hidden' }}>
                  {buildingIdStr && (
                    <MenuItem icon={<FaUserTie style={{ fontSize: '0.8rem', color: '#7c3aed' }} />} label="See visit"
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(null); window.open(managerPreviewUrl(buildingIdStr, { service: v.id }), '_blank', 'noopener,noreferrer'); }} />
                  )}
                  <MenuItem icon={<FaLink style={{ fontSize: '0.8rem' }} />} label="Copy visit link"
                    onClick={async (e) => { e.stopPropagation(); setMenuOpen(null); await copyToClipboard(managerVisitLink(v.id)); onCopied && onCopied(); }} />
                  <MenuItem icon={<FaEdit style={{ fontSize: '0.8rem' }} />} label="Edit Visit"
                    onClick={(e) => { e.stopPropagation(); setMenuOpen(null); onEditVisit(v); }} />
                  <MenuItem icon={<FaTrash style={{ fontSize: '0.8rem' }} />} label="Delete Visit" color="#dc2626" hoverBg="#fef2f2" bordered={false}
                    onClick={(e) => { e.stopPropagation(); setMenuOpen(null); onDeleteVisit(v); }} />
                </div>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Issues Table ─────────────────────────────────────────────────
const ISSUE_GRID = '2fr 100px 100px 1fr 36px';
function IssuesTable({ issues, onClickIssue, onDeleteIssue, buildingIdStr, onCopied }) {
  const [menuOpen, setMenuOpen] = useState(null);
  if (issues.length === 0) return <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', fontSize: '1rem' }}>No issues match this filter.</div>;
  return (
    <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'visible', position: 'relative' }}>
      <div style={{ display: 'grid', gridTemplateColumns: ISSUE_GRID, padding: '0.75rem 1.15rem', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', gap: '0.5rem' }}>
        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Issue</div>
        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Priority</div>
        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</div>
        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Date</div>
        <div />
      </div>
      {issues.map((issue, idx) => {
        const ps = PRIORITY_STYLES[issue.priority] || PRIORITY_STYLES.low;
        const isResolved = issue.status === 'resolved';
        return (
          <div key={issue.id} style={{
            display: 'grid', gridTemplateColumns: ISSUE_GRID, padding: '0.85rem 1.15rem', cursor: 'pointer', alignItems: 'center', gap: '0.5rem',
            borderBottom: idx < issues.length - 1 ? '1px solid #f3f4f6' : 'none', transition: 'background 0.1s', position: 'relative',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f7ff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
            <div onClick={() => onClickIssue(issue)} style={{ fontWeight: 600, color: '#111827', fontSize: '1.05rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '0.5rem' }}>{issue.name}</div>
            <div onClick={() => onClickIssue(issue)}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, padding: '0.2rem 0.55rem', borderRadius: '999px', background: ps.bg, color: ps.color, border: `1px solid ${ps.border}`, whiteSpace: 'nowrap' }}>
                {ps.label}
              </span>
            </div>
            <div onClick={() => onClickIssue(issue)} style={{ fontSize: '0.95rem', color: isResolved ? '#10b981' : '#6b7280', fontWeight: 500 }}>
              {isResolved ? '✅ Resolved' : '🔄 Ongoing'}
            </div>
            <div onClick={() => onClickIssue(issue)} style={{ color: '#6b7280', fontSize: '1rem' }}>{formatDate(issue.dateFound)}</div>
            <div style={{ position: 'relative' }}>
              <button onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === issue.id ? null : issue.id); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '0.25rem', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaEllipsisV />
              </button>
              {menuOpen === issue.id && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 50 }} onClick={() => setMenuOpen(null)} />
                  <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 51, background: '#fff', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', minWidth: 180, overflow: 'hidden' }}>
                    {buildingIdStr && (
                      <MenuItem icon={<FaUserTie style={{ fontSize: '0.8rem', color: '#7c3aed' }} />} label="See issue"
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(null); window.open(managerPreviewUrl(buildingIdStr, { issue: issue.id }), '_blank', 'noopener,noreferrer'); }} />
                    )}
                    <MenuItem icon={<FaLink style={{ fontSize: '0.8rem' }} />} label="Copy issue link"
                      onClick={async (e) => { e.stopPropagation(); setMenuOpen(null); await copyToClipboard(managerIssueLink(issue.id)); onCopied && onCopied(); }} />
                    <MenuItem icon={<FaTrash style={{ fontSize: '0.8rem' }} />} label="Delete Issue" color="#dc2626" hoverBg="#fef2f2" bordered={false}
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(null); onDeleteIssue(issue); }} />
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Shared photo/file upload hooks ───────────────────────────────
function usePhotoUpload() {
  const [photos, setPhotos] = useState([]);
  const photoRef = useRef(null);
  const handlePhotos = useCallback(async (fileList) => {
    for (const file of fileList) {
      if (!file.type.startsWith('image/')) continue;
      try {
        const c = await compressImage(file);
        setPhotos((prev) => [...prev, { type: 'new', name: c.name, dataUrl: c.dataUrl, size: c.size }]);
      } catch (e) { console.error('Compress error:', e); }
    }
  }, []);
  const removePhoto = useCallback((idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx)), []);
  return { photos, setPhotos, photoRef, handlePhotos, removePhoto };
}

function useFileUpload() {
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);
  const handleFileAttachments = useCallback(async (fileList) => {
    for (const file of fileList) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setFiles((prev) => [...prev, { type: 'new', name: file.name, mimeType: file.type, data: base64, size: file.size }]);
      };
      reader.readAsDataURL(file);
    }
  }, []);
  const removeFile = useCallback((idx) => setFiles((prev) => prev.filter((_, i) => i !== idx)), []);
  return { files, setFiles, fileRef, handleFileAttachments, removeFile };
}

function PhotoUploadField({ label, required, photoRef, photos, handlePhotos, removePhoto, onImageClick }) {
  const inputStyle2 = { width: '100%' };
  return (
    <div style={{ marginBottom: '1.15rem' }}>
      <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '1rem', color: '#374151' }}>{label}{required ? ' *' : ''}</label>
      <div onClick={() => photoRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#3b82f6'; }}
        onDragLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
        onDrop={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#e5e7eb'; handlePhotos(Array.from(e.dataTransfer.files)); }}
        style={{ ...inputStyle2, border: '2px dashed #e5e7eb', borderRadius: '12px', padding: '1.25rem', textAlign: 'center', cursor: 'pointer', background: '#fafafa' }}>
        <FaCamera style={{ fontSize: '1.5rem', color: '#9ca3af', marginBottom: '0.3rem' }} />
        <div style={{ fontSize: '1rem', color: '#6b7280' }}>Tap to add photos</div>
      </div>
      <input ref={photoRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={(e) => { handlePhotos(Array.from(e.target.files)); e.target.value = ''; }} />
      {photos.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.65rem' }}>
          {photos.map((p, i) => (
            <div key={i} style={{ position: 'relative', width: 76, height: 76, borderRadius: 8, overflow: 'hidden', border: p.type === 'existing' ? '2px solid #3b82f6' : '2px solid #16a34a' }}>
              <img src={p.type === 'existing' ? p.url : p.dataUrl} alt=""
                onClick={onImageClick ? () => onImageClick(i) : undefined}
                style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: onImageClick ? 'zoom-in' : 'default' }} />
              <button type="button" onClick={() => removePhoto(i)} style={{ position: 'absolute', top: 2, right: 2, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(220,38,38,0.9)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700 }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FileUploadField({ fileRef, files, handleFileAttachments, removeFile }) {
  return (
    <div style={{ marginBottom: '1.15rem' }}>
      <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '1rem', color: '#374151' }}>File Attachments</label>
      <div onClick={() => fileRef.current?.click()}
        style={{ border: '2px dashed #e5e7eb', borderRadius: '12px', padding: '0.85rem', textAlign: 'center', cursor: 'pointer', background: '#fafafa' }}>
        <FaPaperclip style={{ fontSize: '1.2rem', color: '#9ca3af', marginBottom: '0.2rem' }} />
        <div style={{ fontSize: '1rem', color: '#6b7280' }}>Tap to add files</div>
      </div>
      <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" multiple style={{ display: 'none' }} onChange={(e) => { handleFileAttachments(Array.from(e.target.files)); e.target.value = ''; }} />
      {files.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.5rem' }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.7rem', background: '#f3f4f6', borderRadius: '8px', fontSize: '0.95rem', color: '#374151' }}>
              <FaPaperclip style={{ color: '#9ca3af', flexShrink: 0 }} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
              <button type="button" onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 700, fontSize: '1rem' }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const INPUT_STYLE = { width: '100%', padding: '0.85rem', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '1rem', fontFamily: 'inherit', outline: 'none' };
const LABEL_STYLE = { display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '1rem', color: '#374151' };

// ── Issue Modal Form ─────────────────────────────────────────────
function IssueFormModal({ open, buildingId, buildingName, equipment, editingIssue, onDone, onClose }) {
  const [equipmentId, setEquipmentId] = useState('');
  const [issueName, setIssueName] = useState('');
  const [issueDetails, setIssueDetails] = useState('');
  const [dateFound, setDateFound] = useState(todayISO());
  const [priority, setPriority] = useState('');
  const [status, setStatus] = useState('ongoing');
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [materialsNeeded, setMaterialsNeeded] = useState('');
  const [laborNeeded, setLaborNeeded] = useState('');
  const [quotingTechnician, setQuotingTechnician] = useState('');
  const [quotationEmail, setQuotationEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const { photos, setPhotos, photoRef, handlePhotos, removePhoto } = usePhotoUpload();
  const { files, setFiles, fileRef, handleFileAttachments, removeFile } = useFileUpload();
  const { files: internalFiles, setFiles: setInternalFiles, handleFileAttachments: handleInternalFiles } = useFileUpload();
  const isEdit = !!editingIssue;

  useEffect(() => {
    if (!open) return;
    if (editingIssue) {
      setEquipmentId(editingIssue.equipmentId || '');
      setIssueName(editingIssue.name || '');
      setIssueDetails(editingIssue.details || '');
      setDateFound(editingIssue.dateFound || todayISO());
      const pMatch = PRIORITIES.find((p) => p.key === editingIssue.priority);
      setPriority(pMatch ? pMatch.value : '');
      setStatus(editingIssue.status || 'ongoing');
      setScopeOfWork(editingIssue.scopeOfWork || '');
      setMaterialsNeeded(editingIssue.materialsNeeded || '');
      setLaborNeeded(editingIssue.laborNeeded || '');
      setQuotingTechnician(editingIssue.quotingTechnician || '');
      setQuotationEmail('');
      setPhotos((editingIssue.photoUrls || []).filter(Boolean).map((url) => ({ type: 'existing', url })));
      setFiles((editingIssue.fileUrls || []).filter(Boolean).map((url) => ({ type: 'existing', url, name: url.split('/').pop() })));
      setInternalFiles((editingIssue.internalFileUrls || []).filter(Boolean).map((url) => ({ type: 'existing', url, name: url.split('/').pop() })));
    } else {
      setEquipmentId(''); setIssueName(''); setIssueDetails(''); setDateFound(todayISO());
      setPriority(''); setStatus('ongoing'); setScopeOfWork(''); setMaterialsNeeded('');
      setLaborNeeded(''); setQuotingTechnician(''); setQuotationEmail('');
      setPhotos([]); setFiles([]); setInternalFiles([]);
    }
    setErrorMsg(''); setSuccessMsg('');
  }, [open, editingIssue, setPhotos, setFiles, setInternalFiles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!equipmentId || !issueName.trim() || !issueDetails.trim() || !dateFound || !priority) { setErrorMsg('Please fill in all required fields.'); return; }
    if (!isEdit && photos.length === 0) { setErrorMsg('Please add at least one photo.'); return; }
    setSubmitting(true); setErrorMsg(''); setSuccessMsg('');
    try {
      const existingPhotoUrls = photos.filter((p) => p.type === 'existing').map((p) => p.url);
      const newPhotos = photos.filter((p) => p.type === 'new').map((p) => ({ name: p.name, type: 'image/jpeg', data: p.dataUrl.split(',')[1] }));
      const existingFileUrls = files.filter((f) => f.type === 'existing').map((f) => f.url);
      const newFiles = files.filter((f) => f.type === 'new').map((f) => ({ name: f.name, type: f.mimeType, data: f.data }));
      const endpoint = isEdit ? '/api/mechanical/form/update-issue' : '/api/mechanical/form/submit-issue';
      const body = {
        ...(isEdit ? { issueId: editingIssue.id } : { buildingId }),
        equipmentId, issueName, issueDetails, dateFound, priority,
        status: status === 'resolved' ? 'Resolved' : 'Ongoing',
        scopeOfWork, materialsNeeded, laborNeeded, quotingTechnician,
        photos: newPhotos, ...(isEdit ? { existingPhotoUrls } : {}), files: newFiles, existingFileUrls,
        internalFiles: internalFiles.filter((f) => f.type === 'new').map((f) => ({ name: f.name, type: f.mimeType, data: f.data })),
        existingInternalFileUrls: internalFiles.filter((f) => f.type === 'existing').map((f) => f.url),
      };
      const r = await fetch(`${API_BASE_URL}${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || 'Failed to save issue'); }
      const result = await r.json();
      if (quotationEmail.trim()) {
        const issueId = isEdit ? editingIssue.id : result.objectId;
        const eqName = equipment.find((eq) => eq.id === equipmentId)?.name || '';
        try { await fetch(`${API_BASE_URL}/api/mechanical/form/email-issue-copy`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ issueId, technicianEmail: quotationEmail, buildingId, buildingName: buildingName || '', equipmentName: eqName, issueName, issueDetails, scopeOfWork, materialsNeeded, laborNeeded, quotingTechnician, priority, dateFound, isUpdate: isEdit }) }); } catch (_) {}
      }
      setSuccessMsg(isEdit ? 'Issue updated!' : 'Issue submitted!');
      setTimeout(() => onDone(), 600);
    } catch (err) { setErrorMsg(err.message); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit Issue' : 'New Issue'}>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Equipment *</label>
          <select value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)} style={{ ...INPUT_STYLE, cursor: 'pointer' }} required>
            <option value="">Select equipment...</option>
            {equipment.map((eq) => <option key={eq.id} value={eq.id}>{eq.name}{eq.category ? ` (${eq.category})` : ''}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Issue Name *</label>
          <input type="text" value={issueName} onChange={(e) => setIssueName(e.target.value)} placeholder="e.g., Dirty Filters" style={INPUT_STYLE} required />
        </div>
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Issue Details *</label>
          <textarea value={issueDetails} onChange={(e) => setIssueDetails(e.target.value)} placeholder="Detailed description..." rows={3} style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 90 }} required />
        </div>

        <CollapsibleQuotation scopeOfWork={scopeOfWork} setScopeOfWork={setScopeOfWork} materialsNeeded={materialsNeeded} setMaterialsNeeded={setMaterialsNeeded}
          laborNeeded={laborNeeded} setLaborNeeded={setLaborNeeded} quotingTechnician={quotingTechnician} setQuotingTechnician={setQuotingTechnician}
          quotationEmail={quotationEmail} setQuotationEmail={setQuotationEmail}
          internalFiles={internalFiles} setInternalFiles={setInternalFiles} onAddInternalFiles={handleInternalFiles} />

        <div style={{ marginBottom: '1.15rem' }}><label style={LABEL_STYLE}>Date Found *</label><input type="date" value={dateFound} onChange={(e) => setDateFound(e.target.value)} style={INPUT_STYLE} required /></div>
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Status</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <div onClick={() => setStatus((s) => s === 'resolved' ? 'ongoing' : 'resolved')} style={{ width: 52, height: 28, borderRadius: 14, cursor: 'pointer', position: 'relative', transition: 'background 0.25s', background: status === 'resolved' ? '#10b981' : '#d1d5db' }}>
              <div style={{ position: 'absolute', top: 3, left: status === 'resolved' ? 27 : 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left 0.25s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
            <span style={{ fontSize: '1rem', fontWeight: 500, color: status === 'resolved' ? '#10b981' : '#6b7280' }}>{status === 'resolved' ? '✅ Resolved' : '🔄 Ongoing'}</span>
          </div>
        </div>
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Priority *</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
            {PRIORITIES.map((p) => { const sel = priority === p.value; return (
              <button key={p.key} type="button" onClick={() => setPriority(p.value)} style={{ padding: '0.75rem 0.5rem', borderRadius: '10px', cursor: 'pointer', textAlign: 'center', fontWeight: sel ? 700 : 400, fontSize: '1rem', border: sel ? '2px solid #3b82f6' : '2px solid #e5e7eb', background: sel ? '#eff6ff' : '#fff', color: '#111827' }}>{p.label} {p.emoji}</button>
            ); })}
          </div>
        </div>

        <PhotoUploadField label="Photos" required={!isEdit} photoRef={photoRef} photos={photos} handlePhotos={handlePhotos} removePhoto={removePhoto} onImageClick={setLightboxIndex} />
        <FileUploadField fileRef={fileRef} files={files} handleFileAttachments={handleFileAttachments} removeFile={removeFile} />

        {errorMsg && <div style={{ padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '0.95rem', marginBottom: '0.75rem' }}>{errorMsg}</div>}
        {successMsg && <div style={{ padding: '0.75rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.95rem', marginBottom: '0.75rem' }}>{successMsg}</div>}
        <button type="submit" disabled={submitting} style={{ width: '100%', padding: '0.9rem', borderRadius: '10px', border: 'none', fontWeight: 700, fontSize: '1.1rem', cursor: submitting ? 'not-allowed' : 'pointer', background: submitting ? '#93c5fd' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', opacity: submitting ? 0.7 : 1 }}>
          {submitting ? (isEdit ? 'Updating...' : 'Submitting...') : (isEdit ? 'Update Issue' : 'Submit Issue')}
        </button>
      </form>
      <ImageLightbox images={photos.map((p) => (p.type === 'existing' ? p.url : p.dataUrl))} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
    </Modal>
  );
}

const DEFAULT_VISIT_DETAILS = 'Standard monthly maintenance visit. All preventative maintenance tasks have been completed in accordance with the approved maintenance schedule. Inspection reports and checklists will be visible on the dashboard. Any deficiencies or recommended corrective actions identified during this service have been recorded under the Issues section of the Mechanical Preventative Maintenance portal for review and follow-up.';

function defaultVisitName() {
  const now = new Date();
  const month = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  return `${month} Monthly Maintenance`;
}

// ── Visit Modal Form ─────────────────────────────────────────────
// Equipment picker for a visit, grouped by the category already on each
// record. The office pre-books a year of visits at once, and most buildings
// carry 30-odd items of which any one visit touches a handful, so the list has
// to collapse: a category header checks or clears its whole group, and the
// groups start closed with a count rather than 30 rows of checkboxes.
function VisitEquipmentPicker({ equipment, selectedIds, onChange }) {
  const [expanded, setExpanded] = useState(() => new Set());

  const groups = useMemo(() => {
    const byCategory = new Map();
    equipment.forEach((eq) => {
      const key = eq.category || 'Uncategorised';
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key).push(eq);
    });
    return [...byCategory.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [equipment]);

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };

  const toggleGroup = (items, allOn) => {
    const next = new Set(selectedIds);
    items.forEach((eq) => { if (allOn) next.delete(eq.id); else next.add(eq.id); });
    onChange(next);
  };

  if (equipment.length === 0) {
    return <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>No equipment on this building yet.</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <button type="button" onClick={() => onChange(new Set(equipment.map((e) => e.id)))}
          style={{ fontSize: '0.8rem', fontWeight: 600, color: '#2563eb', background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 8, padding: '0.3rem 0.7rem', cursor: 'pointer' }}>
          Select all
        </button>
        <button type="button" onClick={() => onChange(new Set())}
          style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.3rem 0.7rem', cursor: 'pointer' }}>
          Clear
        </button>
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        {groups.map(([category, items], gi) => {
          const chosen = items.filter((eq) => selectedIds.has(eq.id)).length;
          const allOn = chosen === items.length;
          const isOpen = expanded.has(category);
          return (
            <div key={category} style={{ borderTop: gi === 0 ? 'none' : '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 0.75rem', background: '#fafafa' }}>
                <input
                  type="checkbox"
                  checked={allOn}
                  // Partially chosen reads as neither on nor off, so the header
                  // can't imply the whole group is in when only two of twelve are.
                  ref={(el) => { if (el) el.indeterminate = chosen > 0 && !allOn; }}
                  onChange={() => toggleGroup(items, allOn)}
                  aria-label={`All ${category}`}
                  style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                />
                <button type="button" onClick={() => setExpanded((prev) => {
                  const next = new Set(prev);
                  if (next.has(category)) next.delete(category); else next.add(category);
                  return next;
                })}
                  aria-expanded={isOpen}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', textAlign: 'left' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.92rem', color: '#111827' }}>{category}</span>
                  <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    {chosen} of {items.length} {isOpen ? '▴' : '▾'}
                  </span>
                </button>
              </div>
              {isOpen && (
                <div style={{ padding: '0.35rem 0.75rem 0.6rem 2.1rem' }}>
                  {items.map((eq) => (
                    <label key={eq.id} style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', padding: '0.3rem 0', cursor: 'pointer' }}>
                      <input type="checkbox" checked={selectedIds.has(eq.id)} onChange={() => toggleOne(eq.id)}
                        style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.88rem', color: '#374151' }}>
                        {eq.name}
                        {eq.tag && <span style={{ color: '#9ca3af' }}> · {eq.tag}</span>}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VisitFormModal({ open, buildingId, equipment = [], editingVisit, onDone, onClose, onStartVisit }) {
  const [visitName, setVisitName] = useState('');
  const [technician, setTechnician] = useState('');
  const [visitDetails, setVisitDetails] = useState('');
  const [dateVisited, setDateVisited] = useState(todayISO());
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [plannedIds, setPlannedIds] = useState(() => new Set());
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  const isEdit = !!editingVisit;

  useEffect(() => {
    if (!open) return;
    if (editingVisit) {
      setVisitName(editingVisit.name || '');
      setTechnician(editingVisit.technician || '');
      setVisitDetails(editingVisit.details || '');
      setDateVisited(editingVisit.dateVisited || todayISO());
      setPlannedIds(new Set(editingVisit.plannedEquipmentIds || []));
    } else {
      setVisitName(defaultVisitName());
      setTechnician('');
      setVisitDetails(DEFAULT_VISIT_DETAILS);
      setDateVisited(todayISO());
      // Everything, so a visit created without opening the picker behaves the
      // way visits did before it existed.
      setPlannedIds(new Set(equipment.map((eq) => eq.id)));
    }
    setEquipmentOpen(false);
    setErrorMsg(''); setSuccessMsg('');
  }, [open, editingVisit, equipment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!visitName.trim() || !technician.trim() || !visitDetails.trim() || !dateVisited) { setErrorMsg('Please fill in all required fields.'); return; }
    setSubmitting(true); setErrorMsg(''); setSuccessMsg('');
    try {
      // No photo keys on either body. A visit's photos are the ones techs
      // attach to equipment as they work, which add-photos appends to the
      // visit; sending an empty list from here would wipe them.
      if (isEdit) {
        const body = {
          visitId: editingVisit.id,
          equipmentIds: editingVisit.equipmentIds || [],
          plannedEquipmentIds: [...plannedIds],
          visitName, technician, visitDetails, dateVisited,
        };
        const r = await fetch(`${API_BASE_URL}/api/mechanical/form/update-visit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || 'Failed to update visit'); }
        setSuccessMsg('Visit updated!');
        setTimeout(() => onDone(), 500);
      } else {
        const body = {
          buildingId, equipmentIds: [],
          plannedEquipmentIds: [...plannedIds],
          visitName, technician, visitDetails, dateVisited,
        };
        const r = await fetch(`${API_BASE_URL}/api/mechanical/form/submit-visit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || 'Failed to save visit'); }
        const result = await r.json();
        const newVisitId = result.objectId;
        setSuccessMsg('Visit created!');
        setTimeout(() => { onDone(); if (newVisitId) onStartVisit(newVisitId); }, 500);
      }
    } catch (err) { setErrorMsg(err.message); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit Visit' : 'Start New Visit'}>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Visit Name *</label>
          <input type="text" value={visitName} onChange={(e) => setVisitName(e.target.value)} style={INPUT_STYLE} required />
        </div>
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Technician Name *</label>
          <input type="text" value={technician} onChange={(e) => setTechnician(e.target.value)} placeholder="e.g., John Smith" style={INPUT_STYLE} required />
        </div>
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Date of Visit *</label>
          <input type="date" value={dateVisited} onChange={(e) => setDateVisited(e.target.value)} style={INPUT_STYLE} required />
        </div>
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Visit Details *</label>
          <textarea value={visitDetails} onChange={(e) => setVisitDetails(e.target.value)} rows={5} style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 120 }} required />
        </div>

        {/* Which equipment this visit covers. Collapsed by default: most
            visits are booked a year ahead in a batch, and the common case is
            accepting the whole building rather than editing the list. */}
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Equipment for this visit</label>
          <button
            type="button"
            onClick={() => setEquipmentOpen((v) => !v)}
            aria-expanded={equipmentOpen}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.7rem 0.85rem', borderRadius: 10, cursor: 'pointer',
              border: '1px solid #d1d5db', background: '#fff', font: 'inherit', textAlign: 'left',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: '0.92rem', color: '#111827' }}>
              Select equipment for the visit
            </span>
            <span style={{ fontSize: '0.85rem', color: plannedIds.size === 0 ? '#b45309' : '#6b7280', fontWeight: 600 }}>
              {plannedIds.size} of {equipment.length} selected {equipmentOpen ? '▴' : '▾'}
            </span>
          </button>
          {equipmentOpen && (
            <div style={{ marginTop: '0.7rem' }}>
              <VisitEquipmentPicker equipment={equipment} selectedIds={plannedIds} onChange={setPlannedIds} />
            </div>
          )}
          {plannedIds.size === 0 && equipment.length > 0 && (
            <div style={{ fontSize: '0.82rem', color: '#b45309', marginTop: '0.45rem' }}>
              With nothing selected the technician sees every item at this building.
            </div>
          )}
        </div>

        {errorMsg && <div style={{ padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '0.95rem', marginBottom: '0.75rem' }}>{errorMsg}</div>}
        {successMsg && <div style={{ padding: '0.75rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.95rem', marginBottom: '0.75rem' }}>{successMsg}</div>}
        <button type="submit" disabled={submitting} style={{ width: '100%', padding: '0.9rem', borderRadius: '10px', border: 'none', fontWeight: 700, fontSize: '1.1rem', cursor: submitting ? 'not-allowed' : 'pointer', background: submitting ? '#86efac' : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#fff', opacity: submitting ? 0.7 : 1 }}>
          {submitting ? (isEdit ? 'Saving...' : 'Starting...') : (isEdit ? 'Save Changes' : 'Start Visit')}
        </button>
      </form>
    </Modal>
  );
}

// ── Equipment Modal Form ─────────────────────────────────────────
function EquipmentFormModal({ open, buildingId, editingEquipment, onDone, onClose }) {
  const [equipmentName, setEquipmentName] = useState('');
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [model, setModel] = useState('');
  const [tag, setTag] = useState('');
  const [manufactureYear, setManufactureYear] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [active, setActive] = useState(true);
  const [healthScore, setHealthScore] = useState(80);
  const [purpose, setPurpose] = useState('');
  const [details, setDetails] = useState('');
  const [coverPhoto, setCoverPhoto] = useState(null); // { type: 'new'|'existing', dataUrl?, url? }
  const [additionalPhotos, setAdditionalPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const coverRef = useRef(null);
  const addlRef = useRef(null);
  const isEdit = !!editingEquipment;

  useEffect(() => {
    if (!open) return;
    if (editingEquipment) {
      setEquipmentName(editingEquipment.name || '');
      setCategory(editingEquipment.category || '');
      setLocation(editingEquipment.location || '');
      setModel(editingEquipment.model || '');
      setTag(editingEquipment.tag || '');
      setManufactureYear(editingEquipment.manufactureYear || '');
      setQuantity(editingEquipment.quantity || 1);
      setActive(editingEquipment.active !== false);
      setHealthScore(editingEquipment.healthScore ?? 80);
      setPurpose(editingEquipment.purpose || '');
      setDetails(editingEquipment.details || '');
      setCoverPhoto(editingEquipment.photoUrl ? { type: 'existing', url: editingEquipment.photoUrl } : null);
      setAdditionalPhotos((editingEquipment.additionalPhotos || []).filter(Boolean).map((url) => ({ type: 'existing', url })));
    } else {
      setEquipmentName(''); setCategory(''); setLocation(''); setModel(''); setTag('');
      setManufactureYear(''); setQuantity(1); setActive(true); setHealthScore(80);
      setPurpose(''); setDetails(''); setCoverPhoto(null); setAdditionalPhotos([]);
    }
    setErrorMsg(''); setSuccessMsg(''); setDirty(false);
  }, [open, editingEquipment]);

  const [dirty, setDirty] = useState(false);
  const markDirty = useCallback(() => setDirty(true), []);

  const handleCoverPhoto = useCallback(async (file) => {
    if (!file.type.startsWith('image/')) return;
    try {
      const c = await compressImage(file);
      setCoverPhoto({ type: 'new', name: c.name, dataUrl: c.dataUrl });
      setDirty(true);
    } catch (e) { console.error(e); }
  }, []);

  const handleAdditionalPhotos = useCallback(async (fileList) => {
    for (const file of fileList) {
      if (!file.type.startsWith('image/')) continue;
      try {
        const c = await compressImage(file);
        setAdditionalPhotos((prev) => [...prev, { type: 'new', name: c.name, dataUrl: c.dataUrl }]);
        setDirty(true);
      } catch (e) { console.error(e); }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!equipmentName.trim() || !category || !location.trim()) { setErrorMsg('Name, category, and location are required.'); return; }
    setSubmitting(true); setErrorMsg(''); setSuccessMsg('');
    try {
      const newCover = coverPhoto?.type === 'new' ? { name: coverPhoto.name, type: 'image/jpeg', data: coverPhoto.dataUrl.split(',')[1] } : null;
      const existingCoverUrl = coverPhoto?.type === 'existing' ? coverPhoto.url : '';
      const newAddl = additionalPhotos.filter((p) => p.type === 'new').map((p) => ({ name: p.name, type: 'image/jpeg', data: p.dataUrl.split(',')[1] }));
      const existingAddl = additionalPhotos.filter((p) => p.type === 'existing').map((p) => p.url);

      const endpoint = isEdit ? '/api/mechanical/form/update-equipment' : '/api/mechanical/form/submit-equipment';
      const body = {
        ...(isEdit ? { equipmentId: editingEquipment.id } : { buildingId }),
        equipmentName, category, location, model, tag, manufactureYear,
        quantity, active, healthScore, purpose, details,
        coverPhoto: newCover, additionalPhotos: newAddl,
        ...(isEdit ? { existingCoverPhotoUrl: existingCoverUrl, existingAdditionalPhotos: existingAddl } : {}),
      };

      const r = await fetch(`${API_BASE_URL}${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || 'Failed to save'); }
      setSuccessMsg(isEdit ? 'Equipment updated!' : 'Equipment added!');
      setTimeout(() => onDone(), 600);
    } catch (err) { setErrorMsg(err.message); }
    finally { setSubmitting(false); }
  };

  const galleryImages = [
    ...(coverPhoto ? [coverPhoto.type === 'existing' ? coverPhoto.url : coverPhoto.dataUrl] : []),
    ...additionalPhotos.map((p) => (p.type === 'existing' ? p.url : p.dataUrl)),
  ];
  const coverGalleryIndex = 0;
  const addlGalleryOffset = coverPhoto ? 1 : 0;

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit Equipment' : 'Add New Equipment'} warnOnClose={dirty}>
      <form onSubmit={handleSubmit} onChange={markDirty}>
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Equipment Name *</label>
          <input type="text" value={equipmentName} onChange={(e) => { setEquipmentName(e.target.value); markDirty(); }} placeholder="e.g., Rooftop Unit 1, Boiler 2" style={INPUT_STYLE} required />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.15rem' }}>
          <div>
            <label style={LABEL_STYLE}>Category *</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ ...INPUT_STYLE, cursor: 'pointer' }} required>
              <option value="">Select...</option>
              {EQUIPMENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={LABEL_STYLE}>Location *</label>
            <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g., Mechanical Room" style={INPUT_STYLE} required />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.15rem' }}>
          <div><label style={LABEL_STYLE}>Model</label><input type="text" value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g., Carrier 50XC" style={INPUT_STYLE} /></div>
          <div><label style={LABEL_STYLE}>Tag / Asset ID</label><input type="text" value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g., RTU-1" style={INPUT_STYLE} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1.15rem' }}>
          <div><label style={LABEL_STYLE}>Year</label><input type="text" value={manufactureYear} onChange={(e) => setManufactureYear(e.target.value)} placeholder="2018" maxLength={4} style={INPUT_STYLE} /></div>
          <div><label style={LABEL_STYLE}>Qty</label><input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} min={1} style={INPUT_STYLE} /></div>
          <div>
            <label style={LABEL_STYLE}>Status</label>
            <div onClick={() => setActive((a) => !a)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.85rem 0' }}>
              <div style={{ width: 44, height: 24, borderRadius: 12, position: 'relative', background: active ? '#10b981' : '#d1d5db', transition: 'background 0.2s' }}>
                <div style={{ position: 'absolute', top: 2, left: active ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
              </div>
              <span style={{ fontSize: '0.95rem', fontWeight: 500, color: active ? '#10b981' : '#6b7280' }}>{active ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
        </div>
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Health Score: {healthScore}%</label>
          <input type="range" min={0} max={100} value={healthScore} onChange={(e) => setHealthScore(Number(e.target.value))}
            style={{ width: '100%', accentColor: healthScore > 60 ? '#10b981' : healthScore > 30 ? '#f59e0b' : '#ef4444' }} />
        </div>
        <div style={{ marginBottom: '1.15rem' }}><label style={LABEL_STYLE}>Purpose</label><input type="text" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="e.g., Space heating" style={INPUT_STYLE} /></div>
        <div style={{ marginBottom: '1.15rem' }}><label style={LABEL_STYLE}>Details / Notes</label><textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Any additional details..." rows={2} style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 60 }} /></div>

        {/* Cover Photo */}
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Cover Photo</label>
          <div onClick={() => coverRef.current?.click()} style={{ border: '2px dashed #e5e7eb', borderRadius: '10px', padding: '0.85rem', textAlign: 'center', cursor: 'pointer', background: '#fafafa' }}>
            <FaCamera style={{ fontSize: '1.2rem', color: '#9ca3af' }} />
            <div style={{ fontSize: '0.95rem', color: '#6b7280' }}>Tap to upload</div>
          </div>
          <input ref={coverRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { if (e.target.files[0]) handleCoverPhoto(e.target.files[0]); e.target.value = ''; }} />
          {coverPhoto && (
            <div style={{ position: 'relative', width: 80, height: 80, borderRadius: 8, overflow: 'hidden', marginTop: '0.5rem', border: coverPhoto.type === 'existing' ? '2px solid #3b82f6' : '2px solid #16a34a' }}>
              <img src={coverPhoto.type === 'existing' ? coverPhoto.url : coverPhoto.dataUrl} alt=""
                onClick={() => setLightboxIndex(coverGalleryIndex)}
                style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }} />
              <button type="button" onClick={() => setCoverPhoto(null)} style={{ position: 'absolute', top: 1, right: 1, width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'rgba(220,38,38,0.9)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>×</button>
            </div>
          )}
        </div>

        {/* Additional Photos */}
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={LABEL_STYLE}>Additional Photos (Name Plates)</label>
          <div onClick={() => addlRef.current?.click()} style={{ border: '2px dashed #e5e7eb', borderRadius: '10px', padding: '0.65rem', textAlign: 'center', cursor: 'pointer', background: '#fafafa' }}>
            <div style={{ fontSize: '0.95rem', color: '#6b7280' }}>Tap to upload name plates</div>
          </div>
          <input ref={addlRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => { handleAdditionalPhotos(Array.from(e.target.files)); e.target.value = ''; }} />
          {additionalPhotos.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
              {additionalPhotos.map((p, i) => (
                <div key={i} style={{ position: 'relative', width: 64, height: 64, borderRadius: 8, overflow: 'hidden', border: p.type === 'existing' ? '2px solid #3b82f6' : '2px solid #16a34a' }}>
                  <img src={p.type === 'existing' ? p.url : p.dataUrl} alt=""
                    onClick={() => setLightboxIndex(addlGalleryOffset + i)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }} />
                  <button type="button" onClick={() => setAdditionalPhotos((prev) => prev.filter((_, j) => j !== i))} style={{ position: 'absolute', top: 1, right: 1, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(220,38,38,0.9)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700 }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {errorMsg && <div style={{ padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '0.95rem', marginBottom: '0.75rem' }}>{errorMsg}</div>}
        {successMsg && <div style={{ padding: '0.75rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.95rem', marginBottom: '0.75rem' }}>{successMsg}</div>}
        <button type="submit" disabled={submitting} style={{ width: '100%', padding: '0.9rem', borderRadius: '10px', border: 'none', fontWeight: 700, fontSize: '1.1rem', cursor: submitting ? 'not-allowed' : 'pointer', background: submitting ? '#93c5fd' : '#2563eb', color: '#fff', opacity: submitting ? 0.7 : 1 }}>
          {submitting ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Equipment')}
        </button>
      </form>
      <ImageLightbox images={galleryImages} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
    </Modal>
  );
}

// ── Upcoming Jobber Visits ────────────────────────────────────────
function cleanTechName(name) {
  // Jobber user names carry a crew code prefix like "202 Hooman Mahdavi"
  return (name || '').replace(/^\d+\s+/, '');
}

function formatVisitDay(startAt) {
  try {
    return new Date(startAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/Toronto' });
  } catch { return ''; }
}

function formatVisitTime(startAt, endAt) {
  try {
    const opts = { hour: 'numeric', minute: '2-digit', timeZone: 'America/Toronto' };
    const s = new Date(startAt).toLocaleTimeString('en-US', opts);
    const e = endAt ? new Date(endAt).toLocaleTimeString('en-US', opts) : null;
    return e ? `${s} – ${e}` : s;
  } catch { return ''; }
}

function UpcomingJobberVisits({ buildingId }) {
  const [state, setState] = useState({ loading: true, linked: false, job: null, visits: [] });
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/mechanical/jobber/building/${encodeURIComponent(buildingId)}/visits`);
        if (!r.ok) throw new Error(`status ${r.status}`);
        const data = await r.json();
        if (!cancelled) setState({ loading: false, linked: !!data.linked, job: data.job || null, visits: Array.isArray(data.visits) ? data.visits : [] });
      } catch (e) {
        console.warn('[UpcomingJobberVisits] unavailable:', e);
        if (!cancelled) setState({ loading: false, linked: false, job: null, visits: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [buildingId]);

  if (state.loading || !state.linked) return null;

  const { job, visits } = state;

  return (
    <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '1.1rem 1.5rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <button onClick={() => setExpanded((p) => !p)} style={{ background: 'none', border: 'none', padding: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', flex: 1, minWidth: 0 }}>
          <FaCalendarAlt style={{ color: '#2563eb', fontSize: '1.05rem', flexShrink: 0 }} />
          <span style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>Upcoming Visits ({visits.length})</span>
          <FaChevronDown style={{ color: '#6b7280', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }} />
        </button>
        {job?.webUri && (
          <a href={job.webUri} target="_blank" rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', fontWeight: 600, color: '#2563eb', textDecoration: 'none', border: '1px solid #dbeafe', background: '#eff6ff', borderRadius: '8px', padding: '0.35rem 0.65rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Job #{job.jobNumber} <FaExternalLinkAlt style={{ fontSize: '0.7rem' }} />
          </a>
        )}
      </div>
      {expanded && (
        visits.length === 0 ? (
          <div style={{ padding: '0.9rem 0 0.2rem', color: '#9ca3af', fontSize: '0.95rem' }}>
            No upcoming visits scheduled on the master job.
          </div>
        ) : (
          <div style={{ marginTop: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {visits.map((v) => (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', padding: '0.65rem 0.85rem', background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: '10px' }}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: '#eff6ff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', lineHeight: 1 }}>
                    {new Date(v.startAt).toLocaleDateString('en-US', { month: 'short', timeZone: 'America/Toronto' })}
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e40af', lineHeight: 1.15 }}>
                    {new Date(v.startAt).toLocaleDateString('en-US', { day: 'numeric', timeZone: 'America/Toronto' })}
                  </div>
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
                    {formatVisitDay(v.startAt)}
                    <span style={{ color: '#6b7280', fontWeight: 500 }}> · {formatVisitTime(v.startAt, v.endAt)}</span>
                  </div>
                  {v.assignedTo && v.assignedTo.length > 0 && (
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.15rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <FaUser style={{ fontSize: '0.7rem', flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {v.assignedTo.map(cleanTechName).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ── Building Notes ────────────────────────────────────────────────
// Permanent plain-text notes/instructions for the building, stored on the
// Building record in Back4App (mech_notes).
function BuildingNotes({ buildingId, initialNotes }) {
  const [notes, setNotes] = useState(initialNotes || '');
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setNotes(initialNotes || ''); }, [initialNotes]);

  const startEdit = () => { setDraft(notes); setError(null); setEditing(true); };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/mechanical/tech/building/${encodeURIComponent(buildingId)}/notes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: draft }),
      });
      if (!r.ok) throw new Error(`Failed to save (${r.status})`);
      setNotes(draft);
      setEditing(false);
    } catch (e) {
      console.error('[BuildingNotes] Save error:', e);
      setError('Failed to save notes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: '#fffdf5', border: '1px solid #fde68a', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', padding: '1rem 1.5rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <FaStickyNote style={{ color: '#d97706', fontSize: '1rem', flexShrink: 0 }} />
        <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827', flex: 1 }}>Building Notes</span>
        {!editing && (
          <button onClick={startEdit} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: 'none', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.3rem 0.65rem', cursor: 'pointer', color: '#b45309', fontWeight: 600, fontSize: '0.85rem' }}>
            <FaEdit style={{ fontSize: '0.75rem' }} /> {notes ? 'Edit' : 'Add notes'}
          </button>
        )}
      </div>
      {editing ? (
        <div style={{ marginTop: '0.75rem' }}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
            rows={6}
            placeholder="Permanent instructions for this building — access codes location, parking, who to call on site, quirks of the equipment…"
            style={{ width: '100%', padding: '0.75rem', border: '1px solid #fcd34d', borderRadius: '10px', fontSize: '1rem', outline: 'none', background: '#fff', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
          />
          {error && <div style={{ color: '#b91c1c', fontSize: '0.85rem', fontWeight: 600, marginTop: '0.35rem' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
            <button onClick={() => setEditing(false)} disabled={saving}
              style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', color: '#6b7280' }}>
              Cancel
            </button>
            <button onClick={save} disabled={saving}
              style={{ padding: '0.5rem 1.25rem', borderRadius: '8px', border: 'none', background: saving ? '#fbbf24' : '#d97706', cursor: saving ? 'default' : 'pointer', fontWeight: 700, fontSize: '0.9rem', color: '#fff' }}>
              {saving ? 'Saving…' : 'Save notes'}
            </button>
          </div>
        </div>
      ) : notes ? (
        <div style={{ marginTop: '0.6rem', fontSize: '0.98rem', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{notes}</div>
      ) : (
        <div onClick={startEdit} style={{ marginTop: '0.6rem', fontSize: '0.9rem', color: '#9ca3af', cursor: 'pointer' }}>
          No notes yet — add permanent instructions for this building.
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────
export default function MechBuildingDetail() {
  const { buildingId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showVisits, setShowVisits] = useState(true);
  const [showIssues, setShowIssues] = useState(false);
  const [issueFilter, setIssueFilter] = useState('all');
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [visitModalOpen, setVisitModalOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [editingIssue, setEditingIssue] = useState(null);
  const [showEquipment, setShowEquipment] = useState(false);
  const [equipmentModalOpen, setEquipmentModalOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [pdfModal, setPdfModal] = useState({ open: false, url: '', title: '' });
  const [copiedToast, setCopiedToast] = useState(false);
  const [eqMenuOpen, setEqMenuOpen] = useState(null);

  const showCopiedToast = () => {
    setCopiedToast(true);
    setTimeout(() => setCopiedToast(false), 2000);
  };

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/mechanical/tech/building/${encodeURIComponent(buildingId)}/detail`);
      if (!r.ok) throw new Error(`Failed to load building: ${r.status}`);
      setData(await r.json()); setError(null);
    } catch (e) { console.error('[MechBuildingDetail] Error:', e); setError('Failed to load building details.'); }
    finally { setIsLoading(false); }
  }, [buildingId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleIssueDone = () => { setIssueModalOpen(false); setEditingIssue(null); loadData(); };
  const handleVisitDone = () => { setVisitModalOpen(false); setEditingVisit(null); loadData(); };
  const handleDeleteIssue = async (issue) => {
    if (!window.confirm(`Delete "${issue.name}"? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API_BASE_URL}/api/mechanical/tech/issue/${encodeURIComponent(issue.id)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Failed to delete');
      loadData();
    } catch (e) { console.error('Delete error:', e); alert('Failed to delete issue.'); }
  };
  const handleDeleteVisit = async (visit) => {
    if (!window.confirm(`Delete "${visit.name}"? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API_BASE_URL}/api/mechanical/tech/visit/${encodeURIComponent(visit.id)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Failed to delete');
      loadData();
    } catch (e) { console.error('Delete error:', e); alert('Failed to delete visit.'); }
  };

  if (isLoading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4rem 1rem' }}>
      <Spinner animation="border" style={{ color: '#2563eb', marginBottom: '1rem' }}><span className="visually-hidden">Loading...</span></Spinner>
      <p style={{ color: '#6b7280', fontWeight: 500, fontSize: '1.1rem', margin: 0 }}>Loading...</p>
    </div>
  );

  if (error) return (
    <div style={{ padding: '2rem' }}>
      <button onClick={() => navigate('/mech-schedule')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#2563eb', fontWeight: 600, fontSize: '1rem', padding: 0, marginBottom: '1rem' }}><FaArrowLeft /> Back</button>
      <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', color: '#991b1b', fontSize: '1rem' }}>{error}</div>
    </div>
  );

  const { building, visits = [], issues = [], equipment = [] } = data || {};
  const filteredIssues = issues.filter((i) => {
    if (issueFilter === 'ongoing') return i.status !== 'resolved';
    if (issueFilter === 'resolved') return i.status === 'resolved';
    return true;
  });

  return (
    <div style={{ padding: '1.5rem 1.25rem', minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <button onClick={() => navigate('/mech-schedule')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#2563eb', fontWeight: 600, fontSize: '1rem', padding: 0, marginBottom: '1.25rem' }}><FaArrowLeft /> Back</button>

        {/* Building header with stats */}
        <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '1.25rem 1.5rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            {building?.photoUrl && (
              <img
                src={building.photoUrl}
                alt=""
                loading="lazy"
                decoding="async"
                style={{ width: 64, height: 64, borderRadius: 14, objectFit: 'cover', flexShrink: 0, background: '#f3f4f6' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>{building?.name}</h1>
              {building?.address && <p style={{ fontSize: '1rem', color: '#6b7280', margin: '0.2rem 0 0' }}>{building.address}</p>}
              {building?.buildingId && (
                <a
                  href={managerPreviewUrl(building.buildingId)}
                  target="_blank"
                  rel="noreferrer"
                  title="Open the manager dashboard for this building (preview)"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.5rem',
                    fontSize: '0.8rem', fontWeight: 700, color: '#7c3aed', textDecoration: 'none',
                    background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '999px',
                    padding: '0.25rem 0.7rem',
                  }}
                >
                  <FaUserTie style={{ fontSize: '0.7rem' }} /> Manager view <FaExternalLinkAlt style={{ fontSize: '0.6rem' }} />
                </a>
              )}
            </div>
            <div style={{ display: 'flex', gap: '1.25rem', flexShrink: 0 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>{visits.length}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Visits</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>{issues.filter((i) => i.status !== 'resolved').length}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Issues</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>{equipment.length}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Equip.</div>
              </div>
            </div>
          </div>
          {(building?.scheduleFileUrl || building?.contractFileUrl) && (
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.85rem' }}>
              {building.scheduleFileUrl && (
                <button onClick={() => setPdfModal({ open: true, url: building.scheduleFileUrl, title: 'Maintenance Schedule' })}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.65rem 0.75rem', borderRadius: '10px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem', color: '#2563eb', transition: 'background 0.15s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}>
                  <FaFileAlt /> View Schedule
                </button>
              )}
              {building.contractFileUrl && (
                <button onClick={() => setPdfModal({ open: true, url: building.contractFileUrl, title: 'Equipment Contract' })}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.65rem 0.75rem', borderRadius: '10px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem', color: '#2563eb', transition: 'background 0.15s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}>
                  <FaFileAlt /> View Contract
                </button>
              )}
            </div>
          )}
        </div>

        {/* Permanent notes / instructions for the building */}
        <BuildingNotes buildingId={buildingId} initialNotes={building?.notes} />

        {/* Upcoming Jobber visits (only shows when the building is linked to its master job) */}
        <UpcomingJobberVisits buildingId={buildingId} />

        {/* Visits */}
        <div style={{ marginBottom: '2rem' }}>
          <button onClick={() => setShowVisits((p) => !p)} style={{ background: 'none', border: 'none', padding: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
            <FaWrench style={{ color: '#3b82f6', fontSize: '1.1rem' }} />
            <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>Visits ({visits.length})</span>
            <FaChevronDown style={{ color: '#6b7280', transition: 'transform 0.2s', transform: showVisits ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </button>
          <button onClick={() => { setEditingVisit(null); setVisitModalOpen(true); }} style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            padding: '0.9rem', borderRadius: '12px', border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: '1.1rem',
            background: '#16a34a', color: '#fff',
            boxShadow: '0 2px 8px rgba(22,163,74,0.3)', marginBottom: '0.85rem',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(22,163,74,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(22,163,74,0.3)'; }}
          >
            <FaPlus /> Start New Visit
          </button>
          {showVisits && <VisitsTable visits={visits} onClickVisit={(v) => navigate(`/mech-schedule/buildings/${encodeURIComponent(buildingId)}/visits/${encodeURIComponent(v.id)}/checklist`)} onDeleteVisit={handleDeleteVisit} onEditVisit={(v) => { setEditingVisit(v); setVisitModalOpen(true); }} buildingIdStr={building?.buildingId} onCopied={showCopiedToast} />}
        </div>

        {/* Issues */}
        <div>
          <button onClick={() => setShowIssues((p) => !p)} style={{ background: 'none', border: 'none', padding: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
            <FaExclamationTriangle style={{ color: '#f59e0b', fontSize: '1.1rem' }} />
            <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>Issues ({issues.length})</span>
            <FaChevronDown style={{ color: '#6b7280', transition: 'transform 0.2s', transform: showIssues ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </button>
          <button onClick={() => { setEditingIssue(null); setIssueModalOpen(true); }} style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            padding: '0.9rem', borderRadius: '12px', border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: '1.1rem',
            background: '#dc2626', color: '#fff',
            boxShadow: '0 2px 8px rgba(220,38,38,0.3)', marginBottom: '0.85rem',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(220,38,38,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(220,38,38,0.3)'; }}
          >
            <FaPlus /> Report New Issue
          </button>
          {showIssues && (
            <>
              {issues.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  {['all', 'ongoing', 'resolved'].map((f) => (
                    <button key={f} onClick={() => setIssueFilter(f)} style={{
                      padding: '0.5rem 0.85rem', borderRadius: '999px', fontSize: '0.9rem', cursor: 'pointer', textTransform: 'capitalize',
                      border: issueFilter === f ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                      background: issueFilter === f ? '#eff6ff' : '#fff', color: issueFilter === f ? '#2563eb' : '#6b7280', fontWeight: issueFilter === f ? 600 : 400,
                    }}>{f} ({f === 'all' ? issues.length : f === 'ongoing' ? issues.filter((i) => i.status !== 'resolved').length : issues.filter((i) => i.status === 'resolved').length})</button>
                  ))}
                </div>
              )}
              <IssuesTable issues={filteredIssues} onClickIssue={(i) => { setEditingIssue(i); setIssueModalOpen(true); }} onDeleteIssue={handleDeleteIssue} buildingIdStr={building?.buildingId} onCopied={showCopiedToast} />
            </>
          )}
        </div>

        {/* Equipment */}
        <div style={{ marginTop: '2rem' }}>
          <button onClick={() => setShowEquipment((p) => !p)} style={{ background: 'none', border: 'none', padding: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
            <FaCog style={{ color: '#6b7280', fontSize: '1.1rem' }} />
            <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>Equipment ({equipment.length})</span>
            <FaChevronDown style={{ color: '#6b7280', transition: 'transform 0.2s', transform: showEquipment ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </button>
          <button onClick={() => { setEditingEquipment(null); setEquipmentModalOpen(true); }} style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            padding: '0.9rem', borderRadius: '12px', border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: '1.1rem',
            background: '#2563eb', color: '#fff',
            boxShadow: '0 2px 8px rgba(37,99,235,0.3)', marginBottom: '0.85rem',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(37,99,235,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(37,99,235,0.3)'; }}
          >
            <FaPlus /> Add New Equipment
          </button>
          {showEquipment && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
              {equipment.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: '#9ca3af', fontSize: '1rem' }}>No equipment yet.</div>
              ) : (
                equipment.map((eq) => (
                  <div key={eq.id} onClick={() => { setEditingEquipment(eq); setEquipmentModalOpen(true); }}
                    style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '0.85rem', cursor: 'pointer', transition: 'all 0.15s', position: 'relative' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(59,130,246,0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}>
                    {building?.buildingId && (
                      <div style={{ position: 'absolute', top: 6, right: 6, zIndex: 5 }}>
                        <button onClick={(e) => { e.stopPropagation(); setEqMenuOpen(eqMenuOpen === eq.id ? null : eq.id); }}
                          style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.9)', boxShadow: '0 1px 4px rgba(0,0,0,0.15)', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem' }}>
                          <FaEllipsisV />
                        </button>
                        {eqMenuOpen === eq.id && (
                          <>
                            <div style={{ position: 'fixed', inset: 0, zIndex: 50 }} onClick={(e) => { e.stopPropagation(); setEqMenuOpen(null); }} />
                            <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 51, background: '#fff', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', minWidth: 180, overflow: 'hidden' }}>
                              <MenuItem icon={<FaUserTie style={{ fontSize: '0.8rem', color: '#7c3aed' }} />} label="See equipment" bordered={false}
                                onClick={(e) => { e.stopPropagation(); setEqMenuOpen(null); window.open(managerEquipmentPreviewUrl(building.buildingId, eq.id), '_blank', 'noopener,noreferrer'); }} />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {eq.photoUrl ? (
                      <img src={eq.photoUrl} alt="" style={{ width: '100%', height: 100, borderRadius: 8, objectFit: 'cover', marginBottom: '0.5rem', background: '#f3f4f6' }} />
                    ) : (
                      <div style={{ width: '100%', height: 100, borderRadius: 8, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem' }}>
                        <FaCog style={{ fontSize: '2rem', color: '#d1d5db' }} />
                      </div>
                    )}
                    <div style={{ fontWeight: 600, color: '#111827', fontSize: '1rem', marginBottom: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{eq.name}</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{eq.category}{eq.location ? ` · ${eq.location}` : ''}</div>
                    {!eq.active && <div style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: 600, marginTop: '0.2rem' }}>Inactive</div>}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <IssueFormModal open={issueModalOpen} buildingId={buildingId} buildingName={building?.name || ''} equipment={equipment} editingIssue={editingIssue} onDone={handleIssueDone} onClose={() => { setIssueModalOpen(false); setEditingIssue(null); }} />
      <VisitFormModal open={visitModalOpen} buildingId={buildingId} equipment={equipment} editingVisit={editingVisit} onDone={handleVisitDone} onClose={() => { setVisitModalOpen(false); setEditingVisit(null); }}
        onStartVisit={(visitId) => navigate(`/mech-schedule/buildings/${encodeURIComponent(buildingId)}/visits/${encodeURIComponent(visitId)}/checklist`)} />
      <EquipmentFormModal open={equipmentModalOpen} buildingId={buildingId} editingEquipment={editingEquipment}
        onDone={() => { setEquipmentModalOpen(false); setEditingEquipment(null); loadData(); }}
        onClose={() => { setEquipmentModalOpen(false); setEditingEquipment(null); }} />
      <PdfViewerModal open={pdfModal.open} url={pdfModal.url} title={pdfModal.title} onClose={() => setPdfModal({ open: false, url: '', title: '' })} />

      {copiedToast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 10000,
          background: '#111827', color: '#fff', fontWeight: 600, fontSize: '0.9rem',
          padding: '0.6rem 1.2rem', borderRadius: '999px', boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <FaLink style={{ fontSize: '0.75rem' }} /> Link copied
        </div>
      )}
    </div>
  );
}
